import React, {useCallback, useEffect, useState} from 'react';
import {Button, Col, Drawer, Form, Radio, Row, Space, Typography} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import {useAppSelector} from '../../hooks/useStore';
import EngageCurrency from './EngageCurrency';
import InputMoney from './InputMoney';
import {useCreatePurchaseMutation} from '../../store/api/purchaseApi';
import {roundNumberUpToPrecision} from '../../helpers/misc';
import {
    amountCurrentlyOwing,
    currentDepositValue,
    failTransactionText,
    reduceWhenNoDepositsText
} from '../../helpers/text';
import {messageType, showMessages} from '../../helpers/message';
import {selectCurrentProfile} from '../../store/slices/userSlice';
import dayjs from 'dayjs';

import {RadioChangeEvent} from 'antd/es/radio';
import {DepositExchangeDrawerProps} from '../../types/purchaseTypes';
import confirmAction from '../../helpers/confirmAction';

import './DepositHistory.less';
import './DepositExchangeDrawer.less';
import {BaseError} from '../../types/apiTypes';

const DepositExchangeDrawer = ({
                                   typeOfTransaction,
                                   curDepositValue,
                                   accountingBalance
                               }: DepositExchangeDrawerProps) => {
    const [depositAmount, setDepositAmount] = useState<number>(0);
    const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
    const [valueCheckbox, setValueCheckbox] = useState<number | null>(null);
    const [amountInput, setAmountInput] = useState<number | null>(null);

    const mobileMode = window.innerWidth <= 768;
    const [form] = Form.useForm();

    const currentProfile = useAppSelector(selectCurrentProfile);
    const [createPurchase, {isLoading, isSuccess, error, reset}] = useCreatePurchaseMutation();

    const showDrawer = () => {
        // Disable Reduce Deposit drawer if account doesn't have any Current Deposit Value.
        if (typeOfTransaction === 'reduce' && curDepositValue === 0) {
            return showMessages(reduceWhenNoDepositsText, messageType.error);
        } else {
            setDrawerVisible(true);
        }
    };

    const closeDrawer = useCallback(() => {
        form.resetFields();
        setDepositAmount(0);
        setAmountInput(null);
        setDrawerVisible(false);
        setValueCheckbox(null);
        reset();
    }, [form, reset]);

    // Check the Radio button.
    const onChecked = (e: RadioChangeEvent) => {
        const value = Number(e.target.value);
        setValueCheckbox(value);
        setAmountInput(value);
        setDepositAmount(value);
        form.setFieldValue('Deposit Amount', value);

        // Trigger validation after setting the value.
        form.validateFields(['Deposit Amount']);
    };

    // Change the Input field.
    const onInputChange = (value: number) => {
        const amount = roundNumberUpToPrecision(value, 2) ?? 0;
        setDepositAmount(amount);
        setValueCheckbox(null);
        setAmountInput(amount);
        form.setFieldValue('Deposit Amount', amount);
    };

    /* If Amount Currently Owing is more (or equal to) the Deposit Value, or is equal 0 or a credit, disable Amount Currently Owing.
       If both are equal we have to disable one to keep radio buttons values different to handle checkbox logic correctly.
     */
    const disableAmountOwing = (amountOwing: number, depositValue: number) => {
        return amountOwing >= depositValue || amountOwing <= 0;
    };

    const handleFinish = async (values: { 'Deposit Amount': number }) => {
        const depositAmountOnFinish = typeOfTransaction === 'increase'
            ? values['Deposit Amount']
            : amountInput ? amountInput * -1 : 0;

        confirmAction(
            'Are you sure you want to perform this action?',
            '',
            async () => {
                await createPurchase({
                    value: depositAmountOnFinish,
                    account_id: currentProfile?.account?.id,
                    recipient_id: currentProfile?.account?.id,
                    recipient_type: 'account',
                    item_type: 'deposit',
                    purchased_at: dayjs().tz().format('YYYY-MM-DD HH:mm:ssZZ'),
                });
            },
            undefined,
            () => {
                closeDrawer();
                showMessages(failTransactionText, messageType.error);
            }
        );
    };

    useEffect(() => {
        if (isSuccess) {
            showMessages(`Deposit successfully ${typeOfTransaction === 'increase' ? 'added to the account' : 'reduced from the account'}.`, messageType.success);
            closeDrawer();
        }

        if (error) {
            showMessages((error as BaseError)?.data?.message, messageType.error);
        }
    }, [isSuccess, error, closeDrawer, typeOfTransaction]);

    return (
        <>
            {typeOfTransaction === 'increase' ? (
                <Button className="deposit-history-table-increase-btn" type="primary" icon={<PlusOutlined/>}
                        onClick={showDrawer}>
                    Increase Deposit
                </Button>
            ) : (
                <Button className="deposit-history-table-reduce-btn" type="default" onClick={showDrawer}>
                    Reduce Deposit
                </Button>
            )}
            <Drawer
                className="deposit-drawer"
                width={mobileMode ? '100%' : '45%'}
                title={typeOfTransaction === 'increase' ? 'Increase Deposit' : 'Reduce Deposit'}
                placement="right"
                closable={true}
                onClose={closeDrawer}
                open={drawerVisible}
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="deposit-transaction"
                    onFinish={handleFinish}
                    validateMessages={{
                        number: {
                            min: 'Please enter an amount greater or equal to 0.01',
                        },
                        types: {
                            number: ''
                        },
                    }}
                >
                    {typeOfTransaction === 'increase' ? (
                        <Row className="full-width-row">
                            <Col>
                                <Form.Item
                                    name="Deposit Amount"
                                    label="Deposit Amount: "
                                    rules={[{type: 'number', required: true, min: 0.01}]}
                                >
                                    <InputMoney
                                        onChange={onInputChange}
                                        value={amountInput}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    ) : (
                        <div>
                            <Row className="full-width-row">
                                <Col>
                                    <Form.Item
                                        label="Deposit Amount: "
                                        name="Deposit Amount"
                                        rules={[
                                            {
                                                type: 'number',
                                                required: true
                                            },
                                            {
                                                validator: (_, value) => {
                                                    if (value < 0.01) {
                                                        return Promise.reject(new Error("Please enter an amount greater than or equal to 0.01"));
                                                    }
                                                    if (value > curDepositValue) {
                                                        return Promise.reject(new Error(`Please enter an amount less than or equal to ${curDepositValue}`));
                                                    }
                                                    return Promise.resolve();
                                                },
                                            },
                                        ]}
                                    >
                                        <InputMoney
                                            amount={amountInput}
                                            onChange={onInputChange}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Form.Item>
                                        <Radio.Group onChange={onChecked} value={valueCheckbox}>
                                            <Space direction="vertical">
                                                <Radio
                                                    value={accountingBalance}
                                                    disabled={disableAmountOwing(accountingBalance, curDepositValue)}
                                                >
                                                    <div className="deposit-radio-content">
                                                        <EngageCurrency amount={accountingBalance}/>
                                                        <div className="deposit-radio-label">{amountCurrentlyOwing}</div>
                                                    </div>
                                                </Radio>
                                                <Radio value={curDepositValue}>
                                                    <div className="deposit-radio-content">
                                                        <EngageCurrency amount={curDepositValue}/>
                                                        <div className="deposit-radio-label">{currentDepositValue}</div>
                                                    </div>
                                                </Radio>
                                            </Space>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    )}
                    <div className="drawer-footer">
                        <div className="deposit-calculation-container">
                            <Row justify="space-between">
                                <Col>
                                    <Typography.Title level={5} className="deposit-calculation-label">Total
                                        Amount:</Typography.Title>
                                </Col>
                                <Col>
                                    <Typography.Title level={5} className="deposit-calculation-total">
                                        <EngageCurrency
                                            amount={depositAmount ? depositAmount : 0}
                                        />
                                    </Typography.Title>
                                </Col>
                            </Row>
                        </div>
                        <Row>
                            <Button className="deposit-drawer-cancel-btn" onClick={closeDrawer}>Cancel</Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={isLoading}
                            >
                                {typeOfTransaction === 'increase' ? 'Increase Deposit' : 'Reduce Deposit'}
                            </Button>
                        </Row>
                    </div>
                </Form>
            </Drawer>
        </>
    );
};

export default DepositExchangeDrawer;
